
(() => {
  "use strict";

  const STORAGE_KEY = "dawn-wishlist-v1";
  const CACHE = new Map();

  function readItems() {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");

      if (!Array.isArray(stored)) return [];

      return stored.map((item) => {
        // Migrate IDs saved by the earlier version.
        if (typeof item === "string" || typeof item === "number") {
          return { id: String(item), handle: "" };
        }

        return {
          id: String(item.id || ""),
          handle: String(item.handle || ""),
        };
      }).filter((item) => item.id);
    } catch {
      return [];
    }
  }

  function saveItems(items) {
    try {
      localStorage.setItem(
        STORAGE_KEY,
        JSON.stringify(
          [...new Map(items.map((item) => [item.id, item])).values()]
        )
      );
      return true;
    } catch {
      return false;
    }
  }

  function updateUI() {
    const items = readItems();

    document.querySelectorAll("[data-wishlist-count]").forEach((el) => {
      el.textContent = items.length;
      el.hidden = items.length === 0;
    });

    document.querySelectorAll("[data-wishlist-button]").forEach((button) => {
      const item = items.find(
        (entry) => entry.id === String(button.dataset.productId)
      );
      const saved = Boolean(item);

      button.setAttribute("aria-pressed", String(saved));
      button.setAttribute(
        "aria-label",
        `${saved ? "Remove" : "Add"} ${
          button.dataset.productTitle || "product"
        } ${saved ? "from" : "to"} wishlist`
      );
      button.classList.toggle("is-wishlisted", saved);
    });
  }

  function announce(message) {
    let region = document.querySelector("[data-wishlist-announcement]");

    if (!region) {
      region = document.createElement("div");
      region.className = "visually-hidden";
      region.dataset.wishlistAnnouncement = "";
      region.setAttribute("role", "status");
      region.setAttribute("aria-live", "polite");
      document.body.appendChild(region);
    }

    region.textContent = message;
  }

  function toggle(button) {
    const id = String(button.dataset.productId || "");
    const handle = String(button.dataset.productHandle || "");
    if (!id || !handle) return;

    const items = readItems();
    const exists = items.some((item) => item.id === id);

    const next = exists
      ? items.filter((item) => item.id !== id)
      : [...items, { id, handle }];

    if (!saveItems(next)) {
      announce("Could not save the wishlist in this browser.");
      return;
    }

    updateUI();
    announce(exists ? "Removed from wishlist." : "Added to wishlist.");

    if (document.querySelector("[data-wishlist-page]")) {
      renderPage();
    }
  }

  async function getProduct(handle) {
    if (CACHE.has(handle)) return CACHE.get(handle);

    const request = fetch(
      `/products/${encodeURIComponent(handle)}.js`,
      { headers: { Accept: "application/json" } }
    ).then((response) => {
      if (!response.ok) throw new Error("Product could not be loaded.");
      return response.json();
    });

    CACHE.set(handle, request);

    try {
      return await request;
    } catch (error) {
      CACHE.delete(handle);
      throw error;
    }
  }

  function escapeHTML(value) {
    return String(value ?? "").replace(/[&<>"']/g, (char) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;",
    })[char]);
  }

  function money(cents) {
    const currency = window.Shopify?.currency?.active || "INR";

    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
    }).format(Number(cents || 0) / 100);
  }

  function renderCard(item, product) {
    const image = product.featured_image
      ? `<img class="wishlist-card__image"
           src="${escapeHTML(product.featured_image)}"
           alt="${escapeHTML(product.title)}" loading="lazy">`
      : `<div class="wishlist-card__placeholder">No image</div>`;

    const variant = product.variants?.find((v) => v.available)
      || product.variants?.[0];

    const cartButton = product.available && variant
      ? `<button type="button"
           data-wishlist-add-cart="${escapeHTML(variant.id)}">
           Add to cart
         </button>`
      : `<button type="button" disabled>Sold out</button>`;

    const url = product.url || `/products/${product.handle}`;

    return `
      <article class="wishlist-card" data-wishlist-card="${escapeHTML(item.id)}">
        <a class="wishlist-card__image-link" href="${escapeHTML(url)}">
          ${image}
        </a>
        <div class="wishlist-card__content">
          <h2 class="wishlist-card__title">
            <a href="${escapeHTML(url)}">${escapeHTML(product.title)}</a>
          </h2>
          <div class="wishlist-card__price">${money(product.price)}</div>
          <div class="wishlist-card__actions">
            ${cartButton}
            <button type="button" class="wishlist-card__remove"
              data-wishlist-remove="${escapeHTML(item.id)}">
              Remove
            </button>
          </div>
        </div>
      </article>`;
  }

  async function renderPage() {
    const page = document.querySelector("[data-wishlist-page]");
    if (!page) return;

    const grid = page.querySelector("[data-wishlist-grid]");
    const empty = page.querySelector("[data-wishlist-empty]");
    const status = page.querySelector("[data-wishlist-status]");
    const items = readItems();

    grid.innerHTML = "";
    empty.hidden = items.length > 0;
    grid.hidden = items.length === 0;
    status.hidden = items.length === 0;

    if (!items.length) return;

    status.textContent = "Loading your wishlist…";

    const results = await Promise.all(items.map(async (item) => {
      if (!item.handle) return { item, product: null };

      try {
        return { item, product: await getProduct(item.handle) };
      } catch {
        return { item, product: null };
      }
    }));

    grid.innerHTML = results
      .filter((result) => result.product)
      .map(({ item, product }) => renderCard(item, product))
      .join("");

    status.hidden = true;
    grid.hidden = grid.children.length === 0;
    empty.hidden = grid.children.length !== 0;

    const failed = results.some((result) => !result.product);
    if (failed) {
      status.hidden = false;
      status.textContent =
        "Some saved products could not be loaded. Try refreshing the page.";
    }
  }

  async function addToCart(button) {
    const variantId = Number(button.dataset.wishlistAddCart);
    if (!variantId) return;

    const original = button.textContent;
    button.disabled = true;
    button.textContent = "Adding…";

    try {
      const root = window.Shopify?.routes?.root || "/";
      const response = await fetch(`${root}cart/add.js`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
        },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
        }),
      });

      if (!response.ok) {
        const error = await response.json().catch(() => ({}));
        throw new Error(error.description || "Could not add to cart.");
      }

      button.textContent = "Added";
      announce("Product added to cart.");
    } catch (error) {
      button.textContent = error.message || "Try again";
      announce(button.textContent);
    } finally {
      setTimeout(() => {
        button.textContent = original;
        button.disabled = false;
      }, 1500);
    }
  }

  document.addEventListener("click", (event) => {
    const heart = event.target.closest("[data-wishlist-button]");

    if (heart) {
      event.preventDefault();
      event.stopPropagation();
      toggle(heart);
      return;
    }

    const remove = event.target.closest("[data-wishlist-remove]");

    if (remove) {
      const id = String(remove.dataset.wishlistRemove);
      saveItems(readItems().filter((item) => item.id !== id));
      updateUI();
      renderPage();
      announce("Removed from wishlist.");
      return;
    }

    const cart = event.target.closest("[data-wishlist-add-cart]");
    if (cart) addToCart(cart);
  });

  function init() {
    updateUI();
    renderPage();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init, { once: true });
  } else {
    init();
  }

  window.DawnWishlist = {
    getItems: readItems,
    add(id, handle) {
      const items = readItems();
      saveItems([...items, { id: String(id), handle: String(handle) }]);
      updateUI();
      renderPage();
    },
    remove(id) {
      saveItems(readItems().filter((item) => item.id !== String(id)));
      updateUI();
      renderPage();
    },
    refresh: init,
  };
})();
